import { useEffect, useState, useCallback, useMemo } from "react";
import { PageContainer } from "../../sharedStyles";
import { useQuery } from "@apollo/client";
import { GET_JOB_MATCHES, GET_CANDIDATE_MATCHES } from "../../GraphQL/matches/queries";
import { JobMatches, JobMatchesData, CandidateMatches, CandidateMatchesData } from "../../interfaces";
import { useNavigate, useParams } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader } from "@/components/ui/page-header";
import MatchesDetailModal from "./MatchesDetailModal";
import { MatchesTableSkeleton } from "./MatchesTableSkeleton";
import { MatchesFilters } from "./MatchesFilters";
import MatchesMobileView from "./MatchesMobileView";
import { RoleMatchesTable } from './RoleMatchesTable';
import { CandidateMatchesTable } from './CandidateMatchesTable';

// Custom hook for responsive breakpoint
const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) {
      setMatches(media.matches);
    }
    const listener = () => setMatches(media.matches);
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, [matches, query]);

  return matches;
};

const PublicMatches = ({ user }: any) => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const [isOpenModal, setIsOpenModal] = useState(false);
  const [jobMatchesList, setJobMatchesList] = useState([] as JobMatches[]);
  const [candidateMatchesList, setCandidateMatchesList] = useState([] as CandidateMatches[]);
  const [activeMatches, setActiveMatches] = useState<any>("");
  const [tabKey, setTabKey] = useState("roles-matches");

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Sorting state
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedData = <T extends Record<string, any>>(data: T[]): T[] => {
    if (!sortField) return data;

    return [...data].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle nested properties like jobLocation.displayName
      if (sortField.includes('.')) {
        const [parent, child] = sortField.split('.');
        aVal = a[parent]?.[child];
        bVal = b[parent]?.[child];
      }

      // Handle null/undefined values
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return sortDirection === 'asc' ? 1 : -1;
      if (bVal == null) return sortDirection === 'asc' ? -1 : 1;

      // String comparison
      const comparison = String(aVal).localeCompare(String(bVal));
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  const {
    error: jobMatchesError,
    loading: jobMatchesLoading,
    data: jobMatchesData,
    refetch: refetchJobMatches,
  } = useQuery<JobMatchesData>(GET_JOB_MATCHES, {
    variables: { order: { updatedAt: "DESC" } },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "cache-and-network",
  });

  const {
    error: candidateMatchesError,
    loading: candidateMatchesLoading,
    data: candidateMatchesData,
    refetch: refetchCandidateMatches,
  } = useQuery<CandidateMatchesData>(GET_CANDIDATE_MATCHES, {
    variables: { order: { updatedAt: "DESC" } },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    setCandidateMatchesList(candidateMatchesData?.candidateMatches?.items || []);
  }, [candidateMatchesData]);

  useEffect(() => {
    setJobMatchesList(jobMatchesData?.jobMatches?.items || []);
  }, [jobMatchesData]);

  // Filtered job matches
  const filteredJobMatches = useMemo(() => {
    return jobMatchesList.filter((match) => {
      // Text search filter
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        searchTerm === '' ||
        match.clientName?.toLowerCase().includes(searchLower) ||
        match.jobTitle?.toLowerCase().includes(searchLower) ||
        match.candidateJobTitle?.toLowerCase().includes(searchLower) ||
        match.candidateIdealJobTitles?.toLowerCase().includes(searchLower);

      // Status filter
      const matchesStatus =
        statusFilter === 'all' || match.currentStatus === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [jobMatchesList, searchTerm, statusFilter]);

  // Filtered candidate matches
  const filteredCandidateMatches = useMemo(() => {
    return candidateMatchesList.filter((match) => {
      // Text search filter
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        searchTerm === '' ||
        match.candidateName?.toLowerCase().includes(searchLower) ||
        match.candidateJobTitle?.toLowerCase().includes(searchLower) ||
        match.jobTitle?.toLowerCase().includes(searchLower);

      // Status filter
      const matchesStatus =
        statusFilter === 'all' || match.currentStatus === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [candidateMatchesList, searchTerm, statusFilter]);


  /* Deeplinked matches routes */
  const [routeMatchId, setRouteMatchId] = useState<number | null>(null);
  const { matchId } = useParams<{ matchId: string }>();

  useEffect(() => {
    matchId ? setRouteMatchId(Number.parseInt(matchId)) : setRouteMatchId(null);
  }, [matchId]);

  if (routeMatchId !== null && !isOpenModal) {
    if (tabKey === 'roles-matches') {
      const role = jobMatchesList.find((role) => Number.parseInt(role.id) === routeMatchId);
      if (role) {
        setActiveMatches(role);
        setIsOpenModal(true);
      }
    } else {
      const candidate = candidateMatchesList.find((candidate) => Number.parseInt(candidate.id) === routeMatchId);
      if (candidate) {
        setActiveMatches(candidate);
        setIsOpenModal(true);
      }
    }
  }

  const setActiveTab = (tab: string | null) => {
    if (tab) setTabKey(tab);
  };

  const handleOpenMatchDetail = (item: JobMatches | CandidateMatches) => {
    setIsOpenModal(true);
    setActiveMatches(item);
    navigate(`/matches/${item?.id}`);
  }
  const handleCloseModal = () => {
    setIsOpenModal(false);
    setRouteMatchId(null); navigate("/matches");
  };

  // Always refetch both lists to ensure the table shows the latest data
  const refreshTable = useCallback(() => {
    refetchJobMatches?.();
    refetchCandidateMatches?.();
  }, [refetchJobMatches, refetchCandidateMatches]);


  return (
    <PageContainer>
      <MatchesDetailModal
        show={isOpenModal}
        onHide={handleCloseModal}
        onRefresh={refreshTable}
        matchContent={activeMatches}
        tabType={tabKey}
      />
      <PageHeader title="Matches" subtitle="Your connections" />
      <Tabs value={tabKey} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="roles-matches">Role Matches</TabsTrigger>
          <TabsTrigger value="candidates-matches">Candidate Matches</TabsTrigger>
        </TabsList>
        <TabsContent value="roles-matches" style={{ position: 'relative' }}>
          <MatchesFilters
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
            resultCount={filteredJobMatches.length}
            totalCount={jobMatchesList.length}
          />
          {jobMatchesLoading ? (
            <MatchesTableSkeleton type="role" rows={5} />
          ) : jobMatchesError ? (
            <div className="text-center p-4 bg-rose-50 dark:bg-rose-900/20 rounded-lg border border-rose-200 dark:border-rose-800">
              <p className="text-rose-700 dark:text-rose-300">
                Unable to load matches at this time - please try again soon
              </p>
            </div>
          ) : isMobile ? (
            <MatchesMobileView
              matches={filteredJobMatches}
              onSelectMatch={handleOpenMatchDetail}
              matchType="role"
            />
          ) : (
            <RoleMatchesTable
              matches={getSortedData(filteredJobMatches)}
              onMatchClick={handleOpenMatchDetail}
              sortBy={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
              isLoading={jobMatchesLoading}
            />
          )}
        </TabsContent>
        <TabsContent value="candidates-matches">
          <MatchesFilters
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
            resultCount={filteredCandidateMatches.length}
            totalCount={candidateMatchesList.length}
          />
          {candidateMatchesLoading ? (
            <MatchesTableSkeleton type="candidate" rows={5} />
          ) : candidateMatchesError ? (
            <div className="text-center p-4 bg-rose-50 dark:bg-rose-900/20 rounded-lg border border-rose-200 dark:border-rose-800">
              <p className="text-rose-700 dark:text-rose-300">
                Unable to load matches at this time - please try again soon
              </p>
            </div>
          ) : isMobile ? (
            <MatchesMobileView
              matches={filteredCandidateMatches}
              onSelectMatch={handleOpenMatchDetail}
              matchType="candidate"
            />
          ) : (
            <CandidateMatchesTable
              matches={getSortedData(filteredCandidateMatches)}
              onMatchClick={handleOpenMatchDetail}
              sortBy={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
              isLoading={candidateMatchesLoading}
            />
          )}
        </TabsContent>
      </Tabs>
    </PageContainer>
  )
}

export default PublicMatches;
